/**
 * OnboardingWizard — first-login value narrative.
 *
 * The KB Update PDF prescribes an 8-step ladder that gets a new founder to
 * their first CFO Brief. Signup pre-fills company + currency, so those steps
 * are marked done automatically by the backend and shown as ticked here.
 *
 * Design principles:
 *   - Non-blocking: user can dismiss with "Skip for now" at any time.
 *   - Deep-linked: each step has a route; clicking takes the user there.
 *   - Auto-detecting: the backend inspects real workspace state (banks,
 *     clients, budgets, ...) to auto-complete steps.
 *
 * State lives at /api/onboarding/state — persisted per user.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Circle, ArrowRight, X, Sparkles } from "lucide-react";

export default function OnboardingWizard() {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/onboarding/state");
      setState(r.data);
      if (r.data.should_show) {
        // Delay to avoid stepping on the login toast.
        setTimeout(() => setOpen(true), 800);
      }
    } catch {
      // Fail silent — onboarding is optional.
    }
  };

  useEffect(() => {
    load();
  }, []);

  const goToStep = async (step) => {
    if (!step.done) {
      // mark as completed optimistically; the auto-detect will confirm
      // when the user next revisits.
      try {
        await api.post(`/onboarding/complete/${step.id}`);
      } catch {
        // ignore
      }
    }
    setOpen(false);
    navigate(step.route);
  };

  const skip = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding/skip");
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding/complete/brief");
      setOpen(false);
      navigate("/app/dashboard");
    } finally {
      setBusy(false);
    }
  };

  if (!state || !open) return null;

  const { steps = [], progress_pct = 0, completed, total } = state;
  const allDone = completed >= total;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm"
      onClick={skip}
      data-testid="onboarding-wizard"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-border bg-gradient-to-br from-primary/25 via-primary/5 to-transparent px-8 py-7">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
              <Sparkles className="h-3 w-3" /> First steps · {completed}/{total} done
            </div>
            <h2 className="mt-2 font-display text-3xl leading-tight tracking-tight">
              Let&rsquo;s get you to your first CFO Brief.
            </h2>
            <p className="mt-2 max-w-md text-sm text-muted-foreground">
              Skip anything you don&rsquo;t need. Each step unlocks a piece of your live financial picture.
            </p>
          </div>
          <button
            onClick={skip}
            data-testid="onboarding-close"
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-8 pt-5">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary transition-[width] duration-500"
              style={{ width: `${progress_pct}%` }}
              data-testid="onboarding-progress"
            />
          </div>
        </div>

        {/* Steps */}
        <ul className="max-h-[55vh] space-y-2 overflow-y-auto px-8 py-6">
          {steps.map((s, idx) => (
            <li key={s.id} data-testid={`onboarding-step-${s.id}`}>
              <button
                onClick={() => goToStep(s)}
                className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition-colors ${
                  s.done ? "bg-muted/40 text-muted-foreground" : "hover:bg-accent"
                }`}
              >
                {s.done ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                ) : (
                  <Circle className="h-5 w-5 shrink-0 text-muted-foreground/40" />
                )}
                <div className="min-w-0 flex-1">
                  <div className={`text-sm ${s.done ? "line-through" : "font-medium text-foreground"}`}>
                    {idx + 1}. {s.label}
                  </div>
                </div>
                {!s.done && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />}
              </button>
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/20 px-8 py-4">
          <button
            onClick={skip}
            disabled={busy}
            data-testid="onboarding-skip"
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Skip for now
          </button>
          <button
            onClick={finish}
            disabled={busy}
            data-testid="onboarding-finish"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {allDone ? "See my CFO Brief" : "I'll do this later"} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
